import * as React from 'react'
import { castingStoreGetMultiOpeningHours, castingStoreGetStepTitle, StoreProps } from '../../../../services/stores/casting.store'
import { moment } from '../../../../services/utils/moment.service'
import { castingUnserializeSlot } from '../../../../services/models/castings.service'
import { I18nService } from '../../../../services/utils/i18n.service'
import { openabledDatifiedMultiOpeningHoursBetween } from '../../../../services/utils/openables.service'
import CastingAnswerEmployeesAppointmentsSuggestedRangesPickerComponent from '../../../backoffice/castings/answer/employees/casting-answer-employees-appointments-suggested-ranges-picker.component'

const i18nScoped = new I18nService(require('./casting-backoffice-appointment-form.component.yml'))

export default function CastingBackofficeAppointmentFormComponent ({ store, data, data: { slots, appointmentRequiredRanges, requiredPlace, offeredService } }: StoreProps) {
  const castingStartDate = React.useMemo(() => {
    const startDate = moment.min(...slots.flatMap(slot => castingUnserializeSlot(slot).allowed_patterns.map(_ => _.from_hour)))

    // when the customer only picks a start date, but no start time, we suppose the event may be scheduled until very late.
    // => it allows the operator to pick availabilities until the day of the event. 
    if (startDate.hours() === 0 && startDate.minutes() === 0 && offeredService.no_start_time_nor_duration_asked_to_customer) startDate.endOf('day')

    return startDate
  }, [offeredService.no_start_time_nor_duration_asked_to_customer, slots])

  const appointmentEaliestDate = React.useMemo(() => moment().add(offeredService.appointment_duration, 'seconds').startOf('hour').add(window.currentUser?.type === 'Operator' ? 0 : 3, 'hour'), [offeredService.appointment_duration])
  const appointmentLatestDate = React.useMemo(() => window.currentUser?.type === 'Operator' ? castingStartDate : castingStartDate.clone().subtract(24, 'hour'), [castingStartDate])
  const tooLate = window.currentUser?.type !== 'Operator' && appointmentLatestDate.isBefore(moment().add(3, 'hours'))

  const pickableRanges = React.useMemo(() => {
    if (tooLate) return []

    const { multiOpeningHoursUnsafeMidnight } = castingStoreGetMultiOpeningHours(data)
    const momentRanges = openabledDatifiedMultiOpeningHoursBetween(multiOpeningHoursUnsafeMidnight, appointmentEaliestDate, appointmentLatestDate)
    return momentRanges.map(([start, end]) => ({ start_date: start.format(), duration: end.diff(start, 'seconds') }))
  }, [appointmentEaliestDate, appointmentLatestDate, data, tooLate])

  if (tooLate) return i18nScoped.t('too_late_for_appointment')
  
  if (appointmentRequiredRanges === null) {
    const menuItemProps: React.HTMLAttributes<HTMLDivElement> = { className: 'boxPod', style: { margin: '0.5em', padding: '1em', cursor: 'pointer' } }

    return <>
      <h3 className="stepTooltip-title txtS u-mbm">{ castingStoreGetStepTitle(data, 'backoffice-appointment') }</h3>

      <div { ...menuItemProps } onClick={ () => store.nextFocus({ closeOnMobile: true }) } id="js-test-appointment-date-accepted-after-appointment-proposals-sent">
        <p className='form-label u-mtn'>
          <em className='icon-long-arrow-alt-right u-mrs' /> { i18nScoped.t('do_not_provide_availabities') }
        </p>

        <p>{ i18nScoped.t('do_not_provide_availabities_tooltip') }</p>
      </div>

      <div { ...menuItemProps } onClick={ () => store.set({ appointmentRequiredRanges: [], backofficeFillServiceDelivery: requiredPlace === 'customers_place' }) } id="js-test-appointment-availabilities-already-known">
        <p className='form-label u-mtn'>
          <em className='icon-long-arrow-alt-right u-mrs' /> { i18nScoped.t('do_provide_availabities') }
        </p>

        <p>{ i18nScoped.t('do_provide_availabities_tooltip') }</p>
      </div>
    </>
  }

  return <>
    <h3 className="stepTooltip-title txtS u-mbm">
      <a id="js-test-back-no-appointment" className="icon-prev buttonNeutral button--XS u-mrm" onClick={ () => store.set({ appointmentRequiredRanges: null }) }></a>
      { castingStoreGetStepTitle(data, 'backoffice-appointment') }
    </h3>

    <CastingAnswerEmployeesAppointmentsSuggestedRangesPickerComponent
      name='customer'
      enableAnytimeMode={ false }
      appointmentAvailabilities={ pickableRanges }
      selectedRanges={ appointmentRequiredRanges }
      onChange={ val => store.set({ appointmentRequiredRanges: val || [] }) } />
  </>
}
